<template>
  <div class="flex h-full items-center gap-4">
    <Information v-if="unpaidAmount !== undefined">
      <template #trigger>
        <div
          class="relative"
          :class="{
            'text-green-700': !isUnpaid,
            'text-bittersweet-600': isUnpaid,
          }"
        >
          <LandmarkIcon class="size-4" />
          <div class="absolute -bottom-0.5 -right-0.5 bg-white">
            <XIcon v-if="isUnpaid" class="size-2" />
            <CheckIcon v-else class="size-2" />
          </div>
        </div>
      </template>
      <template #modal>
        <template v-if="isUnpaid">
          {{
            t('unpaidTooltip', {
              unpaidAmount: formatCurrency(unpaidAmount ?? 0),
              total: formatCurrency(totalAmount),
            })
          }}
        </template>
        <template v-else>
          {{
            t('fullyPaidTooltip', {
              amount: formatCurrency(totalAmount),
            })
          }}
        </template>
      </template>
    </Information>
    <Information>
      <template #trigger>
        <MailCheckIcon v-if="isSent" class="size-4 text-green-700" />
        <MailXIcon v-else class="size-4 text-gray-500" />
      </template>
      <template #modal>
        <template v-if="isSent">
          {{
            t('sentTooltip', {
              date: formatDate(sentStateDate),
            })
          }}
        </template>
        <template v-else>
          {{ t('notSentTooltip') }}
        </template>
      </template>
    </Information>
  </div>
</template>

<script lang="ts" setup>
import Information from '@/components/Information.vue';
import { formatDate } from '@/hooks/use-formatted-date';
import { formatCurrency } from '@/utils/format-numbers';
import { isZero } from '@/utils/is-zero';
import { CheckIcon, LandmarkIcon, MailCheckIcon, MailXIcon, XIcon } from 'lucide-vue-next';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  unpaidAmount?: number;
  sentStateDate?: string | null;
  totalAmount: number;
}>();

const { t } = useI18n();

const isUnpaid = computed(() => !isZero(props.unpaidAmount));

const isSent = computed(() => !!props.sentStateDate);
</script>

<i18n lang="json">
{
  "en": {
    "unpaidTooltip": "{unpaidAmount} of {total} of the invoice are not yet paid",
    "fullyPaidTooltip": "{amount} fully paid",
    "sentTooltip": "Has been sent on {date}",
    "notSentTooltip": "Has not yet been sent"
  },
  "de": {
    "unpaidTooltip": "{unpaidAmount} von insgesamt {total} der Rechnung wurden noch nicht bezahlt",
    "fullyPaidTooltip": "{amount} vollständig bezahlt",
    "sentTooltip": "Wurde am {date} versandt",
    "notSentTooltip": "Wurde noch nicht versandt"
  },
  "es": {
    "unpaidTooltip": "Pendiente de pago en la factura: {unpaidAmount} de {total}",
    "fullyPaidTooltip": "{amount} pagado por completo",
    "sentTooltip": "Enviada el {date}",
    "notSentTooltip": "Aún no se ha enviado"
  }
}
</i18n>
